import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { Dash, PlotData } from "plotly.js";
import { useMemo } from "react";
import { sorted } from "src/helpers";
import { ReviewerTrackingDailyEntry } from "src/store/api/generatedApi";
import { Location } from "./useLocationSelections";

type DailyReviewerActionsChartProps = Readonly<{
  entries: ReviewerTrackingDailyEntry[];
  selectedLocations: readonly Location[];
  showTotal: boolean;
  isLoading: boolean;
}>;

const LOCATION_DASH_CYCLE: Dash[] = ["dash", "dot", "dashdot", "longdash"];

export const DailyReviewerActionsChart = ({
  entries,
  selectedLocations,
  showTotal,
  isLoading,
}: DailyReviewerActionsChartProps) => {
  const { t } = useTenantTranslation();
  const { data: palette, text } = useTheme().palette;

  const { traces, maxValue } = useMemo(() => {
    const metrics = [
      {
        key: "events_created" as const,
        label: t("eventsCreated"),
        color: palette.green,
      },
      {
        key: "events_deleted" as const,
        label: t("eventsDeleted"),
        color: palette.pink,
      },
      {
        key: "events_modified" as const,
        label: t("eventsModified"),
        color: palette.blue,
      },
    ];

    const sortedSelectedLocations = sorted(
      [...selectedLocations],
      (location) => location.name
    );
    const onlyTotal = showTotal && sortedSelectedLocations.length === 0;

    const locationTraces: Partial<PlotData>[] = sortedSelectedLocations.flatMap(
      (location, locationIndex) => {
        const locationEntries = sorted(
          entries.filter((entry) => entry.location_name === location.name),
          (entry) => entry.footage_date
        );
        const dash =
          LOCATION_DASH_CYCLE[locationIndex % LOCATION_DASH_CYCLE.length];
        return metrics.map((metric) => ({
          x: locationEntries.map((entry) => entry.footage_date),
          y: locationEntries.map((entry) => entry[metric.key]),
          type: "scatter" as const,
          mode: "lines+markers" as const,
          name: `${location.name} — ${metric.label}`,
          marker: { color: metric.color, size: 5 },
          line: { color: metric.color, width: 3, dash },
        }));
      }
    );

    const totalTraces: Partial<PlotData>[] = [];
    if (showTotal) {
      const totalsByDate = new Map<
        string,
        {
          events_created: number;
          events_deleted: number;
          events_modified: number;
        }
      >();
      for (const entry of entries) {
        const existing = totalsByDate.get(entry.footage_date) ?? {
          events_created: 0,
          events_deleted: 0,
          events_modified: 0,
        };
        totalsByDate.set(entry.footage_date, {
          events_created: existing.events_created + entry.events_created,
          events_deleted: existing.events_deleted + entry.events_deleted,
          events_modified: existing.events_modified + entry.events_modified,
        });
      }
      const sortedDates = sorted([...totalsByDate.keys()], (date) => date);
      for (const metric of metrics) {
        totalTraces.push({
          x: sortedDates,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          y: sortedDates.map((date) => totalsByDate.get(date)![metric.key]),
          type: "scatter" as const,
          mode: "lines+markers" as const,
          name: onlyTotal ? metric.label : `${t("total")} — ${metric.label}`,
          marker: { color: metric.color, size: 5 },
          line: { color: metric.color, width: 3 },
        });
      }
    }

    const visibleValues = [
      ...sortedSelectedLocations.flatMap((location) =>
        entries
          .filter((entry) => entry.location_name === location.name)
          .flatMap((entry) => metrics.map((metric) => entry[metric.key]))
      ),
      ...totalTraces.flatMap(
        (trace) => (trace.y as number[] | undefined) ?? []
      ),
    ];
    const max = visibleValues.length > 0 ? Math.max(...visibleValues, 1) : 1;

    return { traces: [...locationTraces, ...totalTraces], maxValue: max };
  }, [entries, selectedLocations, showTotal, t, palette]);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (traces.length === 0 || entries.length === 0) {
    return <Alert severity="info">{t("noDataAvailable")}</Alert>;
  }

  const yPadding = maxValue * 1.1;

  return (
    <Plot
      data={traces}
      layout={{
        autosize: true,
        xaxis: { type: "date" },
        yaxis: {
          title: { text: t("actions") },
          range: [0, yPadding],
          zeroline: true,
          zerolinewidth: 2,
          zerolinecolor: text.primary,
        },
        margin: { l: 60, r: 30, t: 30, b: 60 },
        hovermode: "x unified",
        plot_bgcolor: "transparent",
        paper_bgcolor: "transparent",
        legend: { orientation: "h", y: -0.2 },
      }}
      config={{
        responsive: true,
        displayModeBar: false,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "350px" }}
      useResizeHandler
    />
  );
};
